import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import type { StakingStatsResponse } from "../../types/protocol";

import { API_URLS, DEFAULT_QUERY_OPTIONS, fetchJson } from "./shared";

/**
 * Fetch staking statistics from API
 */
async function fetchStakingStats(signal?: AbortSignal): Promise<StakingStatsResponse> {
    const url = `${API_URLS.SOLOMON_DATA}/api/solomon-protocol/staking/stats`;
    return fetchJson<StakingStatsResponse>(url, signal);
}

/**
 * Hook to fetch global staking statistics
 *
 * Returns:
 * - totalStaked: Total USDv staked
 * - totalShares: Total sUSDv shares
 * - sharePrice: Current share price
 * - stakersCount: Number of stakers
 * - currentVestingInfo: Current vesting period APY info
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useStakingStats();
 * if (data) {
 *   console.log(`Total staked: ${data.totalStaked}`);
 * }
 * ```
 */
export function useStakingStats(): UseQueryResult<StakingStatsResponse> {
    return useQuery<StakingStatsResponse>({
        queryKey: ["staking-stats"],
        queryFn: ({ signal }): Promise<StakingStatsResponse> => fetchStakingStats(signal),
        ...DEFAULT_QUERY_OPTIONS
    });
}
